import { useEffect, useRef, useState } from 'react';

const infos = [
  { icon: 'ri-map-pin-2-line', label: '教室地址', value: '台中市豐原區豐原大道一段 269-1 號' },
  { icon: 'ri-bus-line', label: '交通方式', value: '近豐原火車站，步行約 12 分鐘，或搭乘公車至豐原大道站下車' },
  { icon: 'ri-parking-line', label: '停車資訊', value: '教室附近有路邊停車格，亦可停放機車' },
  { icon: 'ri-time-line', label: '上課時間', value: '週一至週六 10:00–21:00，依預約時間為準' },
];

export default function LocationSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="location" className="py-20 md:py-28 bg-white">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Section Header */}
        <div
          ref={ref}
          className={`mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-6 h-px bg-[#b08060]"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">
              Location
            </span>
          </div>
          <h2
            className="font-serif-tc text-[#2c2420] leading-[1.3] mb-4"
            style={{ fontSize: 'clamp(1.625rem, 3vw, 2.25rem)' }}
          >
            上課地點
          </h2>
          <p className="text-[1.0625rem] leading-[1.75] text-[#5a4e42] max-w-2xl">
            鳴箏教室位於台中豐原，提供安靜舒適的學習環境，讓你能在不受干擾的空間中感受古箏的聲音與觸感。
          </p>
        </div>

        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-start transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Left: Map + Info */}
          <div className="space-y-6">
            {/* Google Map Embed */}
            <div className="rounded-2xl overflow-hidden border border-[#ede5d8]" style={{ height: '300px' }}>
              <iframe
                title="鳴箏教室地圖"
                width="100%"
                height="100%"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src="https://maps.google.com/maps?q=台中市豐原區豐原大道一段269-1號&t=m&z=16&output=embed&iwloc=near&hl=zh-TW"
                className="w-full h-full border-0"
              ></iframe>
            </div>

            {/* Address & Transport Info */}
            <div className="bg-[#faf8f5] rounded-2xl border border-[#ede5d8] p-6 space-y-4">
              {infos.map((info, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#f0e8dc] shrink-0 mt-0.5">
                    <i className={`${info.icon} text-[#7a6248] text-base`}></i>
                  </div>
                  <div>
                    <p className="text-[0.78rem] text-[#9a8470] mb-0.5 tracking-wide">{info.label}</p>
                    <p className="text-[0.9375rem] leading-[1.6] text-[#3a2e22] font-medium">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Map CTA */}
            <a
              href="https://maps.google.com/?q=台中市豐原區豐原大道一段269-1號"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#c8b49a] bg-white hover:bg-[#f5f0e8] text-[#5a4e42] text-[0.9375rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-pin-2-line text-[#b08060]"></i>
              在 Google Maps 中開啟導航
              <i className="ri-external-link-line text-sm text-[#9a8470]"></i>
            </a>
          </div>

          {/* Right: Classroom Info */}
          <div className="space-y-5">
            <h3
              className="font-serif-tc text-[#2c2420] leading-[1.4] mb-6"
              style={{ fontSize: '1.25rem' }}
            >
              教室環境
            </h3>

            {/* Classroom Card */}
            <div className="rounded-2xl border border-[#ede5d8] p-7 bg-[#f5f0e8]">
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white">
                  <i className="ri-building-2-line text-[#7a6248] text-xl"></i>
                </div>
                <span className="px-3 py-1 rounded-full text-[0.75rem] font-medium bg-white text-[#7a6248]">
                  實體教室
                </span>
              </div>
              <h4 className="font-serif-tc text-[#2c2420] text-lg font-semibold mb-3">台中豐原教室</h4>
              <p className="text-[0.9375rem] leading-[1.75] text-[#5a4e42]">
                面對面的學習體驗，感受真實的箏聲與觸感，是最推薦的上課方式。教室環境安靜舒適，不受外界干擾，讓你能完全專注在音樂之中。
              </p>
            </div>

            {/* Note for first-timers */}
            <div className="rounded-2xl bg-[#faf8f5] border border-[#ede5d8] p-6 mt-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className="ri-lightbulb-line text-[#b08060] text-lg"></i>
                </div>
                <div>
                  <p className="text-[0.875rem] font-semibold text-[#3a2e22] mb-1.5">第一次來的話</p>
                  <p className="text-[0.875rem] leading-[1.7] text-[#5a4e42]">
                    歡迎你來教室坐坐，親手感受古箏的重量、弦的觸感和真實的聲音振動。體驗課也一樣，來教室感受最直接的學習氛圍。
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-2">
              <a
                href="https://line.me"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#5a3e22] hover:bg-[#3a2e22] text-white text-[0.9375rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-line-fill text-lg"></i>
                加入 LINE@ 確認上課細節
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}